import { AssignationWithAnonymizedOrder } from "@daytrip/legacy-models";
import type { Location } from "@daytrip/legacy-models";
import type { Compensation } from "@daytrip/legacy-models";
import type { Penalty } from "@daytrip/legacy-models";
import type { Subsidy } from "@daytrip/legacy-models";
import autobind from "autobind-decorator";
import { action, observable } from "mobx";

import { AssignationStatus, AssignationWithOrderOperator } from "../../operators/AssignationWithOrderOperator";
import type { PageRouter } from "../../PageRouter";
import { PageStore } from "../../stores/PageStore";

const LIMIT = 10;

@autobind
export class DeclinedAndCancelledTripsPageStore extends PageStore<PageRouter, {}> {
    @observable
    public isLoading: boolean = true;

    @observable
    public error: string | undefined = undefined;

    @observable
    public declinedAssignationOperators: AssignationWithOrderOperator[] = [];

    @observable
    public declinedIsLoadingAssignationOperators: boolean = false;

    @observable
    public declinedErrorAssignationOperators: string | undefined = undefined;

    @observable
    public haveMoreDeclined: boolean = true;

    @observable
    public cancelledAssignationOperators: AssignationWithOrderOperator[] = [];

    @observable
    public cancelledIsLoadingAssignationOperators: boolean = false;

    @observable
    public cancelledErrorAssignationOperators: string | undefined = undefined;

    @observable
    public haveMoreCancelled: boolean = true;

    @observable
    public subsidies: Array<Subsidy> = [];

    @observable
    public penalties: Array<Penalty> = [];

    @observable
    public compensations: Array<Compensation> = [];

    @observable
    public locations: Array<Location> = [];

    @action
    private setIsLoading(status: AssignationStatus, isLoading: boolean) {
        if (status === AssignationStatus.Declined) {
            this.declinedIsLoadingAssignationOperators = isLoading;
        } else if (status === AssignationStatus.Cancelled) {
            this.cancelledIsLoadingAssignationOperators = isLoading;
        }
    }

    @action
    private setError(status: AssignationStatus, error: string | undefined) {
        if (status === AssignationStatus.Declined) {
            this.declinedErrorAssignationOperators = error;
        } else if (status === AssignationStatus.Cancelled) {
            this.cancelledErrorAssignationOperators = error;
        }
    }

    @action
    private getSkip(status: AssignationStatus) {
        if (status === AssignationStatus.Declined) {
            return this.declinedAssignationOperators?.length;
        }
        if (status === AssignationStatus.Cancelled) {
            return this.cancelledAssignationOperators?.length;
        }
        return undefined;
    }

    @action
    private async fetchAssignations(status: AssignationStatus): Promise<AssignationWithAnonymizedOrder[]> {
        return this.rpcClient.assignation.retrieveMyAssignationWithAnonymizedOrder({
            statusIn: [status],
            limit: LIMIT,
            skip: this.getSkip(status),
        });
    }

    @action
    private async fetchContent(assignations: AssignationWithAnonymizedOrder[]) {
        const alreadyFetchedSubsidyIds = this.subsidies.map((subsidy) => subsidy._id);
        const alreadyFetchedPenaltyIds = this.penalties.map((penalty) => penalty._id);
        const alreadyFetchedCompensationIds = this.compensations.map((compensation) => compensation._id);
        const alreadyFetchedlocationIds = this.locations.map((location) => location._id);

        const subsidyIds: string[] = [];
        const penaltyIds: string[] = [];
        const compensationIds: string[] = [];
        const locationIds: string[] = [];

        assignations.forEach((assignation) => {
            subsidyIds.push(...assignation.subsidyIds.filter((subsidy) => !alreadyFetchedSubsidyIds.includes(subsidy)));
            penaltyIds.push(...assignation.penaltyIds.filter((penalty) => !alreadyFetchedPenaltyIds.includes(penalty)));
            compensationIds.push(
                ...assignation.compensationIds.filter(
                    (compensation) => !alreadyFetchedCompensationIds.includes(compensation),
                ),
            );

            if (!alreadyFetchedlocationIds.includes(assignation.order.originLocationId)) {
                locationIds.push(assignation.order.originLocationId);
            }
            if (!alreadyFetchedlocationIds.includes(assignation.order.destinationLocationId)) {
                locationIds.push(assignation.order.destinationLocationId);
            }
            locationIds.push(
                ...assignation.order.contentLocations
                    .map((cl) => cl.locationId)
                    .filter((location) => !alreadyFetchedlocationIds.includes(location)),
            );
        });

        let holdSubsidies = [];
        if (subsidyIds.length > 0) {
            holdSubsidies = await this.rpcClient.assignation.retrieveSubsidies({ ids: subsidyIds });
        }

        let holdPenalties = [];
        if (penaltyIds.length > 0) {
            holdPenalties = await this.rpcClient.assignation.retrievePenalties({ ids: penaltyIds });
        }

        let holdCompensations = [];
        if (compensationIds.length > 0) {
            holdCompensations = await this.rpcClient.assignation.retrieveCompensations({ ids: compensationIds });
        }

        let holdLocations = [];
        if (locationIds.length > 0) {
            holdLocations = await this.rpcClient.content.retrieveLocations({ ids: locationIds });
        }

        this.subsidies.push(...holdSubsidies);
        this.penalties.push(...holdPenalties);
        this.compensations.push(...holdCompensations);
        this.locations.push(...holdLocations);
    }

    @action
    private findLocation(locationId: string) {
        return this.locations.find((location) => location._id === locationId);
    }

    @action
    private convertAssignation(assignation: AssignationWithAnonymizedOrder): AssignationWithOrderOperator {
        const { order } = assignation;
        const origin = this.findLocation(order.originLocationId)!;
        const destination = this.findLocation(order.destinationLocationId)!;
        const contentLocationIds = order.contentLocations.map((cl) => cl.locationId);
        const contentLocations = this.locations.filter((location) => contentLocationIds.includes(location._id));
        return new AssignationWithOrderOperator(assignation, origin, destination, contentLocations);
    }

    @action
    private async fetchAndConvertAssignations(
        status: AssignationStatus,
    ): Promise<AssignationWithOrderOperator[] | undefined> {
        this.setIsLoading(status, true);
        this.setError(status, undefined);
        try {
            const assingations = await this.fetchAssignations(status);
            await this.fetchContent(assingations);
            return assingations.map((assignation) => this.convertAssignation(assignation));
        } catch (e) {
            console.error(JSON.stringify(e, undefined, 2), e);
            this.setError(status, "Something went wrong!");
        } finally {
            this.setIsLoading(status, false);
        }
        return undefined;
    }

    @action
    public async loadMoreDeclinedAssignations() {
        const assignations = await this.fetchAndConvertAssignations(AssignationStatus.Declined);
        if (assignations) {
            this.declinedAssignationOperators.push(...assignations);
            this.haveMoreDeclined = assignations?.length === LIMIT;
        }
    }

    @action
    public async loadMoreCancelledAssignations() {
        const assignations = await this.fetchAndConvertAssignations(AssignationStatus.Cancelled);
        if (assignations) {
            this.cancelledAssignationOperators.push(...assignations);
            this.haveMoreCancelled = assignations?.length === LIMIT;
        }
    }

    @action
    public async onFetchData() {
        this.isLoading = true;
        this.error = undefined;
        try {
            const [declinedAssignationOperators, cancelledAssignationOperators] = await Promise.all([
                this.fetchAssignations(AssignationStatus.Declined),
                this.fetchAssignations(AssignationStatus.Cancelled),
            ]);
            await this.fetchContent([...declinedAssignationOperators, ...cancelledAssignationOperators]);
            this.declinedAssignationOperators.push(
                ...declinedAssignationOperators.map((assignation) => this.convertAssignation(assignation)),
            );
            this.cancelledAssignationOperators.push(
                ...cancelledAssignationOperators.map((assignation) => this.convertAssignation(assignation)),
            );
            this.haveMoreDeclined = declinedAssignationOperators.length === LIMIT;
            this.haveMoreCancelled = cancelledAssignationOperators.length === LIMIT;
        } catch (e) {
            console.error(JSON.stringify(e, undefined, 2), e);
            this.error = "Something went wrong";
        } finally {
            this.isLoading = false;
        }
    }

    public isDataFetched(): this is DeclinedAndCancelledTripsPageStore {
        return !this.isLoading;
    }
}
